import React, {useMemo, useState, useEffect, useCallback} from 'react';
import {isEmpty} from 'ramda';
import {ActionTypeLabels, NA, ResourceTypeLabels, UserRoleLabels, UserRole, ActionType, ResourceType, StatusBadgeVariant, SortOptionLabels, SortOption} from '@/constants';
import {TextInput, SelectInput, Text, Button, DateRangeInput, StatusBadge, Icon} from '@/ui-kits';
import {SelectInputitem, DataTableColumn, AuditLog, ExportAuditLogsRequestPayload, GetAuditLogsRequestPayload} from '@/interface';
import {DataTable, TableCellTooltip, JsonValueModal, ExportWarningModal} from '@/components';
import {formatConsultationDate, downloadFileFromUrl, enumToSelectOptions} from '@/utils/common_functions';
import {useDispatch, useSelector} from 'react-redux';
import {
  getAuditLogsRequest,
  resetAuditLogsMessage,
  getAccessModulesListRequest,
} from '@/services/redux/slices/superAdminDashboardSlice';
import {
  auditLogsDataSelector,
  auditLogsSuccessSelector,
  auditLogsFailureSelector,
  auditLogsCurrentPageSelector,
  auditLogsTotalPagesSelector,
  auditLogsTotalResultSelector,
  accessModulesListSelector,
  userStatus,
} from '@/services/redux/selectors';
import {useToast} from '@/hooks';
import {ErrorCodes, getErrorMessage, SuccessCodes} from '@/utils/getMessages';
import {resetMessage} from '@/utils/common_functions';
import {exportAuditLogs} from '@/services/api';

// ===========================
// constants
// ===========================
const userRoleFilterOptions: SelectInputitem[] = enumToSelectOptions(UserRole, UserRoleLabels);
const actionTypeFilterOptions: SelectInputitem[] = enumToSelectOptions(ActionType, ActionTypeLabels);
// const sortOptionFilterOptions: SelectInputitem[] = enumToSelectOptions(SortOption, SortOptionLabels);

// const statusFilterOptions: SelectInputitem[] = [
//   {id: 1, label: 'Success'},
//   {id: 0, label: 'Failed'},
// ];

// ===========================
// helper functions
// ===========================
const parseJsonValue = (value: any): any => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
};
const formatMetadata = (value: any): string => {
  const parsed = parseJsonValue(value);
  if (!parsed || typeof parsed !== 'object') return NA;
  const formattedPairs = Object.entries(parsed)
    .map(([key, val]) => {
      const formattedKey = key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return `${formattedKey} : ${val}`;
    })
    .join('\n');
  return formattedPairs || NA;
};

const isNonPrimitive = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  const parsed = parseJsonValue(value);
  if (typeof parsed === 'string') return false;
  // Use isEmpty from ramda to disallow empty objects like {}
  if (typeof parsed === 'object' && (Array.isArray(parsed) || parsed.constructor === Object)) {
    return !isEmpty(parsed);
  }
  return false;
};

const formatStringValue = (value: any): string => {
  if (value === null || value === undefined) return '';
  const parsed = parseJsonValue(value);
  if (typeof parsed === 'string') return parsed;
  if (typeof parsed === 'object' && parsed !== null) {
    const keys = Object.keys(parsed);
    if (keys.length === 1 && typeof parsed[keys[0]] === 'number') {
      return String(parsed[keys[0]]);
    }
  }
  return typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
};

const renderJsonValueCell = (opts: {
  value: any;
  columnTitle: string;
  onOpenModal: (value: any, title: string, oldValue?: any) => void;
  oldValueForDiff?: any;
}) => {
  const {value, columnTitle, onOpenModal, oldValueForDiff} = opts;

  if (value === null || value === undefined) {
    return (
      <Text className="font-FigtreeSemiBold text-ui_input_border_dark whitespace-nowrap truncate">
        -
      </Text>
    );
  }

  const parsed = parseJsonValue(value);
  if (parsed !== null && typeof parsed === 'object' && isEmpty(parsed)) {
    return (
      <Text className="font-FigtreeSemiBold text-ui_input_border_dark whitespace-nowrap truncate">
        -
      </Text>
    );
  }

  if (isNonPrimitive(value)) {
    return (
      <button
        onClick={e => {
          e.stopPropagation();
          onOpenModal(value, columnTitle, oldValueForDiff);
        }}
        className="cursor-pointer hover:opacity-70 transition-opacity flex-shrink-0">
        <Icon type="eye" className="text-ui_input_border_dark size-5" />
      </button>
    );
  }

  return (
    <TableCellTooltip message={formatStringValue(value)}>
      <Text className="font-FigtreeSemiBold text-ui_input_border_dark whitespace-nowrap truncate cursor-help">
        {formatStringValue(value)}
      </Text>
    </TableCellTooltip>
  );
};


// ===========================
// component
// ===========================
export const AuditLogScreen: React.FC = () => {
  // ===========================
  // hooks
  // ===========================
  const dispatch = useDispatch();
  const {showToast} = useToast();

  // ===========================
  // selectors
  // ===========================
  const auditLogsData = useSelector(auditLogsDataSelector);
  const auditLogsSuccess = useSelector(auditLogsSuccessSelector) as SuccessCodes;
  const auditLogsFailure = useSelector(auditLogsFailureSelector) as ErrorCodes;
  const auditLogsCurrentPage = useSelector(auditLogsCurrentPageSelector);
  const auditLogsTotalPages = useSelector(auditLogsTotalPagesSelector);
  const auditLogsTotalResults = useSelector(auditLogsTotalResultSelector);
  const isLoading = useSelector(userStatus);
  const accessModuleOptions = useSelector(accessModulesListSelector);

  // ===========================
  // states
  // ===========================
  const [search, setSearch] = useState('');
  const [selectedUserRoleFilter, setSelectedUserRoleFilter] = useState<SelectInputitem | null>(null);
  // const [selectedStatusFilter, setSelectedStatusFilter] = useState<SelectInputitem | null>(null);
  const [selectedAccessModuleFilter, setSelectedAccessModuleFilter] = useState<SelectInputitem | null>(null);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [modalValue, setModalValue] = useState<any>(null);
  const [modalOldValue, setModalOldValue] = useState<any>(undefined);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedActionTypeFilter, setSelectedActionTypeFilter] = useState<SelectInputitem | null>(null);
  // const [selectedSortOptionFilter, setSelectedSortOptionFilter] = useState<SelectInputitem | null>(null);
  const [isExportWarningModalOpen, setIsExportWarningModalOpen] = useState(false);
  // ===========================
  // computed values
  // ===========================
  const auditLogs: AuditLog[] = useMemo(() => {
    if (!auditLogsData || !Array.isArray(auditLogsData)) {
      return [];
    }
    return auditLogsData;
  }, [auditLogsData]);

  const hasActiveFilters = useMemo(
    () =>
      search ||
      selectedUserRoleFilter ||
      // selectedStatusFilter !== null ||
      selectedAccessModuleFilter ||
      fromDate ||
      toDate ||
      selectedActionTypeFilter,
      // selectedSortOptionFilter,
    [search, selectedUserRoleFilter, /* selectedStatusFilter, */ selectedAccessModuleFilter, fromDate, toDate, selectedActionTypeFilter /* , selectedSortOptionFilter */],
  );

  const columns: DataTableColumn<AuditLog>[] = useMemo(
    () => [
      {
        name: 'id',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">LOG ID</Text>,
        render: row => (
          <Text className="font-FigtreeSemiBold text-ui_input_border_dark whitespace-nowrap truncate">
            {row?.id ?? NA}
          </Text>
        ),
        headerAlign: 'left',
        width: {minWidth: '100px', maxWidth: '120px'},
        align: 'left',
      },
      {
        name: 'created_at',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">TIMESTAMP</Text>,
        render: row => (
          <Text className="font-FigtreeSemiBold text-ui_input_border_dark whitespace-nowrap">
            {row?.created_at ? formatConsultationDate(row.created_at, 'long') : NA}
          </Text>
        ),
        headerAlign: 'left',
        width: {minWidth: '180px', maxWidth: '200px'},
        align: 'left',
      },
      {
        name: 'user.id',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">USER ID</Text>,
        render: row => (
          <Text className="font-FigtreeSemiBold text-ui_input_border_dark whitespace-nowrap truncate">
            {row?.user?.id ?? NA}
          </Text>
        ),
        headerAlign: 'center',
        width: {minWidth: '130px', maxWidth: '150px'},
        align: 'center',
      },
      {
        name: 'user.role',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">USER ROLE</Text>,
        render: row => (
          <Text className="font-FigtreeSemiBold text-ui_input_border_dark whitespace-nowrap truncate">
            {row?.user?.role !== undefined ? UserRoleLabels[row.user.role as UserRole] || NA : NA}
          </Text>
        ),
        headerAlign: 'center',
        width: {minWidth: '150px', maxWidth: '180px'},
        align: 'center',
      },
      {
        name: 'access_module',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">ACCESS MODULE</Text>,
        render: row => {
          const accessModule = row?.access_module?.label ?? NA;
          return (
            <TableCellTooltip message={accessModule}>
              <Text className="font-FigtreeSemiBold text-ui_input_border_dark whitespace-nowrap truncate cursor-help">
                {accessModule}
              </Text>
            </TableCellTooltip>
          );
        },
        headerAlign: 'center',
        width: {minWidth: '250px', maxWidth: '300px'},
        align: 'center',
      },
      {
        name: 'action_type',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">ACTION TYPE</Text>,
        render: row => {
          const actionType = row?.action_type as ActionType | undefined;
          return (
            <Text className="font-FigtreeSemiBold text-ui_input_border_dark whitespace-nowrap truncate">
              {actionType !== undefined ? ActionTypeLabels[actionType] || NA : NA}
            </Text>
          );
        },
        headerAlign: 'center',
        width: {minWidth: '150px', maxWidth: '180px'},
        align: 'center',
      },
      {
        name: 'resource_type',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">RESOURCE TYPE</Text>,
        render: row => {
          const resourceType = row?.resource_type as ResourceType | undefined;
          return (
            <Text className="font-FigtreeSemiBold text-ui_input_border_dark whitespace-nowrap truncate">
              {resourceType !== undefined ? ResourceTypeLabels[resourceType] || NA : NA}
            </Text>
          );
        },
        headerAlign: 'center',
        width: {minWidth: '200px', maxWidth: '250px'},
        align: 'center',
      },
      {
        name: 'resource_id',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">RESOURCE ID</Text>,
        render: row => (
          <Text className="font-FigtreeSemiBold text-ui_input_border_dark whitespace-nowrap truncate">
            {row?.resource_id ?? NA}
          </Text>
        ),
        headerAlign: 'center',
        width: {minWidth: '170px', maxWidth: '230px'},
        align: 'center',
      },
      {
        name: 'old_value',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">OLD VALUE</Text>,
        render: row => {
          return renderJsonValueCell({
            value: row?.old_value,
            columnTitle: 'OLD VALUE',
            onOpenModal: handleOpenModal,
          });
        },
        headerAlign: 'center',
        width: {minWidth: '150px', maxWidth: '200px'},
        align: 'center',
      },
      {
        name: 'new_value',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">NEW VALUE</Text>,
        render: row => {
          return renderJsonValueCell({
            value: row?.new_value,
            columnTitle: 'NEW VALUE',
            onOpenModal: handleOpenModal,
            // oldValueForDiff: row?.old_value,
          });
        },
        headerAlign: 'center',
        width: {minWidth: '150px', maxWidth: '200px'},
        align: 'center',
      },
      {
        name: 'status',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">STATUS</Text>,
        render: row => (
          <StatusBadge
            className="!font-FigtreeRegular"
            status={row?.status ? StatusBadgeVariant.GREEN : StatusBadgeVariant.RED}
            variant="secondary">
            {row?.status ? 'Success' : 'Failed'}
          </StatusBadge>
        ),
        headerAlign: 'center',
        width: {minWidth: '120px', maxWidth: '150px'},
        align: 'center',
      },
      {
        name: 'device.ip',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">IP ADDRESS</Text>,
        render: row => (
          <Text className="font-FigtreeSemiBold text-ui_input_border_dark whitespace-nowrap truncate">
            {row?.device?.ip ?? NA}
          </Text>
        ),
        headerAlign: 'center',
        width: {minWidth: '180px', maxWidth: '200px'},
        align: 'center',
      },
      {
        name: 'device.agent',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">USER AGENT</Text>,
        render: row => {
          const userAgent = row?.device?.agent ?? NA;
          return (
            <TableCellTooltip message={userAgent}>
              <Text className="font-FigtreeSemiBold text-ui_input_border_dark whitespace-nowrap truncate cursor-help">
                {userAgent}
              </Text>
            </TableCellTooltip>
          );
        },
        headerAlign: 'center',
        width: {minWidth: '100px', maxWidth: '200px'},
        align: 'center',
      },
      {
        name: 'device.metadata',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">METADATA</Text>,
        render: row => {
          const metadata = formatMetadata(row?.device?.metadata);
          return (
            <TableCellTooltip message={metadata}>
              <Text className="font-FigtreeSemiBold text-ui_input_border_dark whitespace-nowrap truncate cursor-help">
                {metadata}
              </Text>
            </TableCellTooltip>
          );
        },
        headerAlign: 'center',
        width: {minWidth: '100px', maxWidth: '200px'},
        align: 'center',
      },
    ],
    [],
  );

  // ===========================
  // functions
  // ===========================
  const buildPayload = useCallback(
    (): GetAuditLogsRequestPayload => {
      const formatDate = (date: Date | null) => (date ? date.toISOString().split('T')[0] : undefined);
      const payload: GetAuditLogsRequestPayload = {
        page,
      };
      if (search) {
        payload.search_text = search;
      }
      if (fromDate) {
        payload.start_date = formatDate(fromDate);
      }
      if (toDate) {
        payload.end_date = formatDate(toDate);
      } 
      if (selectedUserRoleFilter?.id) {
        payload.role = selectedUserRoleFilter.id;
      }
      if (selectedAccessModuleFilter?.id) {
        payload.access_module = selectedAccessModuleFilter.id;
      }
      // if (selectedStatusFilter?.id) {
      //   payload.status = selectedStatusFilter.id === 1 ? true : selectedStatusFilter.id === 0 ? false : undefined;
      // }
      if (selectedActionTypeFilter?.id) {
        payload.action_type = selectedActionTypeFilter.id;
      }
      // if (selectedSortOptionFilter?.id) {
      //   payload.sort = selectedSortOptionFilter.id === SortOption.DESC ? 'desc' : 'asc';
      // }
      return payload;
    },
    [search, selectedUserRoleFilter, /* selectedStatusFilter, */ selectedAccessModuleFilter, fromDate, toDate, page, selectedActionTypeFilter /* , selectedSortOptionFilter */],
  );

  const fetchAuditLogs = useCallback(() => {
    dispatch(getAuditLogsRequest(buildPayload()));
  }, [buildPayload, dispatch]);

  const resetFilter = () => {
    setPage(1);
    setSearch('');
    setSelectedUserRoleFilter(null);
    // setSelectedStatusFilter(null);
    setSelectedAccessModuleFilter(null);
    setFromDate(null);
    setToDate(null);
    setSelectedActionTypeFilter(null);
    // setSelectedSortOptionFilter(null);
  };

  const handleOpenModal = (value: any, title: string, oldValue?: any) => {
    setModalValue(value);
    setModalOldValue(oldValue);
    setModalTitle(title);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalValue(null);
    setModalOldValue(undefined);
    setModalTitle('');
  };

  const handleExport = async (exportType: 'csv' | 'excel') => {
    // Check if filters are applied
    if (!hasActiveFilters) {
      setIsExportWarningModalOpen(true);
      return;
    }

    try {
      setIsExporting(true);
      const payload: ExportAuditLogsRequestPayload = {
        ...buildPayload(),
        export_type: exportType,
      };
      const response = await exportAuditLogs(payload);
      const blob = new Blob([response.data], {
        type:
          exportType === 'csv'
            ? 'text/csv'
            : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      downloadFileFromUrl(url, `audit_logs.${exportType === 'csv' ? 'csv' : 'xlsx'}`);
      window.URL.revokeObjectURL(url);
      showToast('Export completed successfully', 'success');
    } catch (error: any) {
      console.error('Export error:', error);
      showToast(error?.response?.data?.content?.message || 'Failed to export logs', 'failure');
    } finally {
      setIsExporting(false);
    }
  };

  const handleApplyFilters = () => {
    setIsExportWarningModalOpen(false);
    // Scroll to filters section
    const filtersElement = document.querySelector('.grid.grid-cols-2.sm\\:flex');
    if (filtersElement) {
      filtersElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // ===========================
  // side effects
  // ===========================
  useEffect(() => {
    const timeout = setTimeout(fetchAuditLogs, 500);
    return () => clearTimeout(timeout);
  }, [fetchAuditLogs]);

  useEffect(() => {
    if (auditLogsSuccess) {
      resetMessage(() => {
        dispatch(resetAuditLogsMessage());
      });
    }
  }, [auditLogsSuccess, dispatch]);

  useEffect(() => {
    if (auditLogsFailure && auditLogsFailure !== 'E-10044') {
      showToast(getErrorMessage(auditLogsFailure as ErrorCodes), 'failure');
      resetMessage(() => {
        dispatch(resetAuditLogsMessage());
      });
    }
  }, [auditLogsFailure, dispatch, showToast]);

  useEffect(() => {
    // Fetch access modules list if not already loaded
    if (accessModuleOptions.length === 0) {
      dispatch(getAccessModulesListRequest());
    }
  }, [dispatch, accessModuleOptions.length]);

  // ===========================
  // render
  // ===========================
  return (
    <div className="p-2 sm:py-4 md:p-6 flex flex-col grow gap-4">
      <div className="flex items-center justify-between gap-4">
        <Text variant="heading" className="text-ui_blue">
          Complete immutable record of all system activities.
        </Text>
        <Button
          title="Export"
          variant="primary"
          className="h-[40px] px-6 text-sm flex items-center gap-2"
          onClick={() => handleExport('excel')}
          disabled={isExporting}
          leftIcon="white_download_btn"
          iconClassName="size-4"
        />
      </div>

      <div className="grid grid-cols-2 sm:flex flex-wrap gap-2 z-[100]">
        <TextInput
          value={search}
          onChangeText={setSearch}
          leftIcon="search_blue"
          className="w-full sm:min-w-[330px] sm:max-w-[400px] col-span-full"
          wrapperClassName="px-2 border"
          inputClassName="!pr-0"
          placeholder="Search by Log ID, Resource ID & Resource Type"
        />
        <DateRangeInput
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          className="sm:max-w-[255px] w-full col-span-full sm:col-span-1"
          wrapperClassName="border"
          placeholder="Timestamp"
        />
        <SelectInput
          value={selectedUserRoleFilter?.id || ''}
          onChange={setSelectedUserRoleFilter}
          className="sm:max-w-[200px] w-full"
          wrapperClassName="border"
          placeholder="User Role"
          options={userRoleFilterOptions}
        />
        {/* <SelectInput
          value={selectedStatusFilter?.id ?? ''}
          onChange={setSelectedStatusFilter}
          className="sm:max-w-[200px] w-full"
          wrapperClassName="border"
          placeholder="Status"
          options={statusFilterOptions}
        /> */}
        <SelectInput
          value={selectedAccessModuleFilter?.id || ''}
          onChange={setSelectedAccessModuleFilter}
          className="sm:max-w-[250px] w-full"
          wrapperClassName="border"
          placeholder="Access Module"
          options={accessModuleOptions}
          showSearch
        />
        <SelectInput
          value={selectedActionTypeFilter?.id || ''}
          onChange={setSelectedActionTypeFilter}
          className="sm:max-w-[200px] w-full"
          wrapperClassName="border"
          placeholder="Action Type"
          options={actionTypeFilterOptions}
        />
        {/* <SelectInput
          value={selectedSortOptionFilter?.id || ''}
          onChange={setSelectedSortOptionFilter}
          className="sm:max-w-[200px] w-full"
          wrapperClassName="border"
          placeholder="Sort Option"
          options={sortOptionFilterOptions}
        /> */}
        {hasActiveFilters && (
          <Button title="Reset Filters" variant="secondary" onClick={resetFilter} />
        )}
      </div>

      <DataTable
        data={auditLogs}
        columns={columns}
        totalPages={auditLogsTotalPages || 1}
        currentPage={auditLogsCurrentPage || 1}
        totalResult={auditLogsTotalResults || auditLogs.length}
        onPageChange={setPage}
        loading={isLoading}
        stickyHeader
      />

      <JsonValueModal
        open={isModalOpen}
        onClose={handleCloseModal}
        value={modalValue}
        title={modalTitle}
        oldValue={modalOldValue}
      />

      {/* Export Warning Modal */}
      <ExportWarningModal
        open={isExportWarningModalOpen}
        onClose={() => setIsExportWarningModalOpen(false)}
        onApplyFilters={handleApplyFilters}
      />
    </div>
  );
};
